import { useMemo } from 'react';
import { getFindDepartment, useFindDepartment } from './Departments';
import { GetServerSidePropsContext } from 'next';
import { Cache } from '@/data/types/Cache';
import { getPriceBy } from '@/data/Content/Prices';
import { ProductType } from '@/data/types/Product';

export interface InfoElectronico {
	hasOfferDeContado: boolean;
	prices: {
		offerDpVale: undefined;
		offerDeContado: string | undefined;
		strikedDeContado: string | undefined;
	};
}

export const getIsDpElectronico = async (
	cache: Cache,
	productId: string,
	context: GetServerSidePropsContext
) => {
	const data = await getFindDepartment(cache, { catentries: productId }, context);

	const isDpElectronico = (() => {
		if (!data) {
			return false;
		}

		const product = Object.entries(data?.departments).find(
			([_, value]) => value === 'electronicos'
		);

		return !!product;
	})();

	return { isDpElectronico, data };
};

export const getInfoElectronico = async (
	cache: Cache,
	sku: ProductType,
	context: GetServerSidePropsContext
) => {
	const { isDpElectronico } = await getIsDpElectronico(cache, sku.id, context);

	if (!isDpElectronico) {
		return { isDpElectronico, infoElectronico: undefined };
	}

	const dpElectronicosPrices = await getPriceBy(cache, { catalogEntryId: sku.id }, context).catch(
		() => undefined
	);

	if (!dpElectronicosPrices) {
		return { isDpElectronico, infoElectronico: undefined };
	}

	const infoElectronico = {
		hasOfferDeContado: sku['x_prices.field1'] ? true : false,
		prices: {
			offerDpVale: undefined,
			offerDeContado: dpElectronicosPrices.EntitledPrice?.at(0)
				?.UnitPrice?.at(0)
				?.price?.value?.toString(),
			strikedDeContado: sku['x_prices.field1'],
		},
	};

	return { isDpElectronico, infoElectronico };
};

export const useIsDpElectronico = (productId?: string) => {
	const { data, ...rest } = useFindDepartment({
		catentries: productId,
	});

	const isDpElectronico = useMemo(() => {
		if (!data) {
			return false;
		}

		const product = Object.entries(data?.departments).find(
			([_, value]) => value === 'electronicos'
		);

		return !!product;
	}, [data]);

	return {
		isDpElectronico,
		data,
		...rest,
	};
};
